import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import api from "../../lib/api"
import { getErrorMessage, getFieldError } from "../../lib/errors"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { StyledSelect, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import LessonEditor from "../../components/admin/LessonEditor"
import type { ContentAssetOption } from "../../components/admin/ContentAssetPicker"
import type { PendingVideo } from "../../components/admin/VideoAssociationPicker"
import { LoadState } from "@/components/ui/LoadState"

type NewLessonType = 'youtube' | 'private_video' | 'reading' | 'assignment' | 'document'

type LessonEditorValue = {
  title: string
  description: string
  youtube_url: string
  duration_minutes: number
  order_index: number
  is_preview: boolean
  is_private_video: boolean
}

type Lesson = {
  id: string
  title: string
  description?: string | null
  youtubeUrl?: string | null
  youtubeVideoId?: string | null
  orderIndex: number
  durationMinutes?: number | null
  isPreview: boolean
  isPrivateVideo: boolean
  videoId?: string | null
  videoTitle?: string | null
  videoStatus?: string | null
  documentId?: string | null
  documentTitle?: string | null
  sectionId?: string | null
  lessonType?: 'video' | 'reading' | 'assignment' | 'document'
  documentPath?: string | null
}

type CourseSection = {
  id: string
  title: string
}

type AdminDocument = {
  id: string
  title: string
  course?: { id: string } | null
  lesson?: { id: string } | null
}

function typeFromLesson(lesson: Lesson): NewLessonType {
  if (lesson.isPrivateVideo || lesson.videoStatus) return 'private_video'
  if (lesson.lessonType === 'reading') return 'reading'
  if (lesson.lessonType === 'assignment') return 'assignment'
  if (lesson.lessonType === 'document') return 'document'
  return 'youtube'
}

// ── Icons (stroke-based, 16px grid, currentColor) ──────────

function IconPlay({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 16 16" fill="none">
      <path d="M5 3.5v9l8-4.5-8-4.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}

function IconFilm({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="3" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 3v10M10.5 3v10" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  )
}

function IconFileText({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 16 16" fill="none">
      <path d="M4 2.5h5.5L12 5v8.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M5.5 8h5M5.5 10.3h5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}

function IconBookOpen({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 16 16" fill="none">
      <path d="M8 4.5c-1-1-2.6-1.5-4.5-1.5v8.5c1.9 0 3.5.5 4.5 1.5m0-8.5c1-1 2.6-1.5 4.5-1.5v8.5c-1.9 0-3.5.5-4.5 1.5m0-8.5v8.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconClipboard({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 16 16" fill="none">
      <rect x="3.5" y="3" width="9" height="11" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M6 3V2.3h4V3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M5.5 7.5h5M5.5 10h5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}

function IconCheck({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="12" height="10" viewBox="0 0 10 8" fill="none">
      <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconChevronDown({ className = "" }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const TYPE_TILES = [
  { value: "youtube", label: "YouTube", description: "Pega el enlace de un video público", icon: <IconPlay /> },
  { value: "private_video", label: "Video subido", description: "Asocia un video ya cargado a R2", icon: <IconFilm /> },
  { value: "document", label: "Documento (PDF)", description: "Asocia un PDF de la biblioteca", icon: <IconFileText /> },
  { value: "reading", label: "Lectura", description: "Texto libre, sin archivo adjunto", icon: <IconBookOpen /> },
  { value: "assignment", label: "Tarea", description: "Entrega evaluable manualmente", icon: <IconClipboard /> },
] as const

const TYPE_LABELS: Record<NewLessonType, string> = {
  youtube: "YouTube",
  private_video: "Video subido",
  document: "Documento (PDF)",
  reading: "Lectura",
  assignment: "Tarea",
}

const FORM_ID = "lesson-editor-form"

function ChecklistRow({ done, label, optional }: { done: boolean; label: string; optional?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {done ? (
        <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-success-500">
          <IconCheck />
        </span>
      ) : (
        <span aria-hidden="true" className="h-4 w-4 shrink-0 rounded-full border-[1.5px] border-gray-300 dark:border-gray-600" />
      )}
      <span className={`text-sm ${done ? "text-gray-700 dark:text-gray-300" : "text-gray-400"}`}>
        <span className="sr-only">{done ? "Completo: " : "Pendiente: "}</span>
        {label}
        {optional && !done && <span className="text-gray-300 dark:text-gray-600"> — opcional</span>}
      </span>
    </div>
  )
}

export default function LessonFormPage() {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(lessonId)
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const [sections, setSections] = useState<CourseSection[]>([])
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [sectionId, setSectionId] = useState(searchParams.get("section") ?? "")
  const [editingSection, setEditingSection] = useState(false)
  const [lessonType, setLessonType] = useState<NewLessonType>('youtube')
  const [liveValue, setLiveValue] = useState<LessonEditorValue | null>(null)

  // Content association (video-manager-lesson-association-improvements): picking a
  // video/document here no longer calls the API — it's just pending state, applied
  // atomically when the lesson is saved (see onSubmit).
  const [pendingVideo, setPendingVideo] = useState<PendingVideo>(null)
  const [videoFieldError, setVideoFieldError] = useState<string | undefined>()

  const [documents, setDocuments] = useState<AdminDocument[]>([])
  const [loadingDocuments, setLoadingDocuments] = useState(false)
  const [pendingDocumentId, setPendingDocumentId] = useState<string | null>(null)
  const [documentFieldError, setDocumentFieldError] = useState<string | undefined>()
  const [uploadingNewDocument, setUploadingNewDocument] = useState(false)

  const editingLesson = useMemo(
    () => (lessonId ? lessons.find((l) => l.id === lessonId) ?? null : null),
    [lessonId, lessons],
  )

  const initialValue = useMemo<LessonEditorValue>(
    () => ({
      title: editingLesson?.title ?? "",
      description: editingLesson?.description ?? "",
      youtube_url: editingLesson?.youtubeUrl ?? "",
      duration_minutes: Number(editingLesson?.durationMinutes ?? 0),
      order_index: Number(editingLesson?.orderIndex ?? lessons.length + 1),
      is_preview: editingLesson?.isPreview ?? false,
      is_private_video: editingLesson?.isPrivateVideo ?? false,
    }),
    [editingLesson, lessons.length],
  )

  const load = useCallback(async () => {
    if (!courseId) return
    setLoading(true)
    setError("")
    try {
      const [sectionsRes, lessonsRes] = await Promise.all([
        api.get(`/academy/courses/admin/${courseId}/sections`),
        api.get(`/academy/lessons/course/${courseId}`),
      ])
      setSections(sectionsRes.data?.data ?? [])
      const loadedLessons: Lesson[] = lessonsRes.data?.data ?? []
      setLessons(loadedLessons)

      if (lessonId) {
        const found = loadedLessons.find((l) => l.id === lessonId)
        if (!found) {
          setError("Lección no encontrada")
        } else {
          setLessonType(typeFromLesson(found))
          setSectionId(found.sectionId ?? '')
        }
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [courseId, lessonId])

  const loadDocuments = useCallback(async () => {
    if (!courseId) return
    setLoadingDocuments(true)
    try {
      const res = await api.get("/academy/admin/documents?limit=200&page=1")
      const all: AdminDocument[] = res.data?.data?.documents ?? []
      setDocuments(all.filter((d) => d.course?.id === courseId || d.lesson?.id === lessonId))
    } catch {
      setDocuments([])
    } finally {
      setLoadingDocuments(false)
    }
  }, [courseId, lessonId])

  useEffect(() => {
    void load()
  }, [load])

  // Deferred association: seed the pending video/document from the lesson's
  // current one when it first loads, so opening an existing lesson shows what's
  // already associated without any of it being "saved" again until submit.
  useEffect(() => {
    if (!editingLesson) return
    setPendingVideo(editingLesson.videoId ? { id: editingLesson.videoId, title: editingLesson.videoTitle ?? '' } : null)
    setPendingDocumentId(editingLesson.documentId ?? null)
  }, [editingLesson?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lessonType === 'document') void loadDocuments()
  }, [lessonType, loadDocuments])

  function goBack() {
    navigate(`/academy/courses/${courseId}`)
  }

  async function onSubmit(value: LessonEditorValue) {
    if (!courseId) return
    if (!isEdit && !sectionId) {
      setError("Selecciona una sección — las lecciones no se pueden crear fuera de una sección.")
      return
    }
    setSaving(true)
    setError("")
    try {
      const isYoutube = lessonType === 'youtube'
      const isPrivate = lessonType === 'private_video'
      const isDocument = lessonType === 'document'

      setVideoFieldError(undefined)
      setDocumentFieldError(undefined)

      const payload = {
        title: value.title,
        description: value.description || undefined,
        youtube_url: isYoutube ? (value.youtube_url || undefined) : undefined,
        duration_minutes: value.duration_minutes,
        order_index: value.order_index,
        is_preview: value.is_preview,
        is_private_video: isPrivate || value.is_private_video,
        lesson_type: (lessonType === 'youtube' || lessonType === 'private_video') ? 'video' : lessonType,
        section_id: sectionId || undefined,
        // Deferred association (video-manager-lesson-association-improvements):
        // only sent for the active content type, applied atomically server-side.
        video_id: isPrivate ? (pendingVideo?.id ?? null) : undefined,
        document_id: isDocument ? (pendingDocumentId ?? null) : undefined,
      }

      if (isEdit && lessonId) {
        await api.put(`/academy/lessons/${lessonId}`, { ...payload, course_id: courseId })
        navigate(`/academy/courses/${courseId}`)
      } else {
        const res = await api.post("/academy/lessons", { course_ids: [courseId], ...payload })
        const newLessonId = res.data?.data?.id
        if (newLessonId) {
          navigate(`/academy/courses/${courseId}/lessons/${newLessonId}/edit`)
        } else {
          navigate(`/academy/courses/${courseId}`)
        }
      }
    } catch (err) {
      // 409/400 on video_id/document_id: keep the form open with the rest of the
      // edits intact, surface the conflict next to the picker instead of losing
      // the in-progress edit to a generic top-level error.
      const videoConflict = getFieldError(err, 'video_id')
      const documentConflict = getFieldError(err, 'document_id')
      if (videoConflict) {
        setVideoFieldError(videoConflict)
      } else if (documentConflict) {
        setDocumentFieldError(documentConflict)
      } else {
        setError(getErrorMessage(err))
      }
    } finally {
      setSaving(false)
    }
  }

  async function onUploadNewDocument(file: File) {
    if (!lessonId) return
    setUploadingNewDocument(true)
    try {
      const uploadInit = await api.post("/academy/admin/documents/upload", {
        title: editingLesson?.title || file.name,
        filename: file.name,
        content_type: file.type || "application/pdf",
        lesson_id: lessonId,
      })
      const uploadData = uploadInit.data?.data as { document_id: string; upload_url: string; upload_headers: Record<string, string> }
      const uploadResult = await fetch(uploadData.upload_url, {
        method: "PUT",
        headers: uploadData.upload_headers,
        body: file,
      })
      if (!uploadResult.ok) {
        throw new Error(`Upload fallido (${uploadResult.status})`)
      }
      toastSuccess("PDF subido y asociado a esta lección")
      setPendingDocumentId(uploadData.document_id)
      await Promise.all([load(), loadDocuments()])
    } catch (err) {
      toastError(err)
    } finally {
      setUploadingNewDocument(false)
    }
  }

  const documentOptions: ContentAssetOption[] = documents.map((d) => ({ id: d.id, label: d.title }))

  const currentSection = sections.find((s) => s.id === sectionId)

  // ── Vista previa / checklist ────────────────────────────────
  const titleDone = Boolean(liveValue?.title.trim())
  const descriptionDone = Boolean(liveValue?.description.trim())
  const contentDone =
    lessonType === "youtube" ? Boolean(liveValue?.youtube_url.trim())
    : lessonType === "private_video" ? Boolean(pendingVideo)
    : lessonType === "document" ? Boolean(pendingDocumentId)
    : null // reading/assignment: no external content to associate
  const activeTile = TYPE_TILES.find((t) => t.value === lessonType)

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Contenido",
        { label: "Cursos", to: `/academy/courses/${courseId}` },
        isEdit ? "Editar lección" : "Nueva lección",
      ]}
      title={isEdit ? "Editar lección" : "Nueva lección"}
      subtitle="Asocia contenido ya existente y escribe las notas para el estudiante."
      tabs={[]}
      metrics={[]}
      actions={
        editingSection ? (
          <div className="flex items-center gap-2">
            <StyledSelect value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
              {!sectionId && <option value="">Selecciona una sección…</option>}
              {isEdit && !editingLesson?.sectionId && <option value="">Sin sección</option>}
              {sections.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </StyledSelect>
            <button type="button" onClick={() => setEditingSection(false)} className={`min-h-11 ${btnSecondary}`}>Listo</button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditingSection(true)}
            className="flex min-h-11 items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:bg-gray-800"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-gray-500 dark:bg-gray-900 dark:text-gray-400">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
                <path d="M2 6.5h12" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </span>
            <span className="text-left">
              <span className="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Sección</span>
              <span className="block text-sm font-medium text-gray-800 dark:text-white">
                {currentSection?.title ?? "Selecciona una sección…"}
              </span>
            </span>
            <IconChevronDown className="ml-1 text-gray-400" />
          </button>
        )
      }
    >
      {loading ? (
        <LoadState />
      ) : (
        <div className="space-y-5">
        <div className="flex items-start gap-5">
          {/* MAIN COLUMN */}
          <div className="min-w-0 flex-1 space-y-5">
            {/* Tipo de contenido */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <p className="text-sm font-semibold text-gray-800 dark:text-white/90">Tipo de contenido</p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Elige qué trae esta lección — el contenido se asocia, no se escribe aquí.
              </p>
              <div role="radiogroup" aria-label="Tipo de contenido" className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {TYPE_TILES.map((tile) => {
                  const selected = lessonType === tile.value
                  return (
                    <button
                      key={tile.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setLessonType(tile.value)}
                      className={`relative min-h-11 rounded-2xl border p-4 text-left transition-colors ${
                        selected
                          ? "border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-500/10"
                          : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60"
                      }`}
                    >
                      {selected && (
                        <span aria-hidden="true" className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 shadow-sm">
                          <IconCheck />
                        </span>
                      )}
                      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                        selected ? "bg-brand-100 text-brand-600 dark:bg-brand-500/20 dark:text-brand-300" : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                      }`}>
                        {tile.icon}
                      </span>
                      <p className={`mt-2.5 text-sm font-semibold ${selected ? "text-brand-700 dark:text-brand-300" : "text-gray-800 dark:text-white"}`}>
                        {tile.label}
                      </p>
                      <p className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">{tile.description}</p>
                    </button>
                  )
                })}
              </div>
            </div>

            <LessonEditor
              formId={FORM_ID}
              initialValue={initialValue}
              lessonType={lessonType}
              error={error}
              onSubmit={onSubmit}
              onChange={setLiveValue}
              lessonId={lessonId ?? null}
              pendingVideo={pendingVideo}
              onChangeVideo={setPendingVideo}
              videoFieldError={videoFieldError}
              documentAssets={documentOptions}
              pendingDocumentId={pendingDocumentId}
              loadingDocumentAssets={loadingDocuments}
              onChangeDocumentId={setPendingDocumentId}
              documentFieldError={documentFieldError}
              uploadingNewDocument={uploadingNewDocument}
              onUploadNewDocument={onUploadNewDocument}
            />
          </div>

          {/* STICKY RAIL */}
          <div className="sticky top-5 hidden w-[300px] shrink-0 flex-col gap-4 lg:flex">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Vista previa</p>

              <div className="mb-3.5 flex items-center gap-2.5 rounded-xl bg-gray-50 p-3 dark:bg-gray-800/60">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-600 dark:bg-brand-500/20 dark:text-brand-300">
                  {activeTile?.icon}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-gray-800 dark:text-white">
                    {liveValue?.title.trim() || "Sin título"}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{TYPE_LABELS[lessonType]}</span>
                </span>
              </div>

              <div className="flex flex-col gap-2.5">
                <ChecklistRow done label="Tipo de contenido" />
                <ChecklistRow done={Boolean(sectionId)} label={currentSection ? `Sección — ${currentSection.title}` : "Sección"} />
                <ChecklistRow done={titleDone} label="Título" />
                {contentDone !== null && (
                  <ChecklistRow done={contentDone} label={`Contenido asociado`} />
                )}
                <ChecklistRow done={descriptionDone} label="Resumen" optional />
              </div>
            </div>

            <div className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <button type="submit" form={FORM_ID} disabled={saving} className={`min-h-11 ${btnSave}`}>
                {saving ? "Guardando..." : "Guardar lección"}
              </button>
              <button type="button" onClick={goBack} className={`min-h-11 ${btnSecondary}`}>Cancelar</button>
            </div>
          </div>
        </div>

          {/* Mobile fallback actions — the sticky rail is desktop-only (lg:flex above) */}
          <div className="flex justify-end gap-2 lg:hidden">
            <button type="button" onClick={goBack} className={`min-h-11 ${btnSecondary}`}>Cancelar</button>
            <button type="submit" form={FORM_ID} disabled={saving} className={`min-h-11 ${btnSave}`}>
              {saving ? "Guardando..." : "Guardar lección"}
            </button>
          </div>
        </div>
      )}
    </ModuleLayout>
  )
}
